import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    useEffect,
} from 'react'
import {
    ThemeProvider as StyledThemeProvider,
    DefaultTheme,
} from 'styled-components'
import { lightTheme, darkTheme } from './theme.ts'
import type {
    ThemeContextType,
    ThemeType,
    ThemeProviderProps,
} from './types.ts'

interface ChromeStorageLocal {
    get(
        key: string,
        callback: (result: Record<string, string | undefined>) => void,
    ): void
    set(items: Record<string, string>, callback: () => void): void
}

interface ChromeRuntime {
    lastError?: {
        message: string
    }
}

interface Chrome {
    storage: {
        local: ChromeStorageLocal
    }
    runtime: ChromeRuntime
}

const THEME_STORAGE_KEY = 'taxing-theme'

function isExtensionContext(): boolean {
    if (typeof window === 'undefined') return false
    const chrome = (window as unknown as Record<string, unknown>).chrome as
        | Chrome
        | undefined
    return !!chrome && !!chrome.storage && !!chrome.storage.local
}

const getStoredTheme = async (defaultTheme: ThemeType): Promise<boolean> => {
    try {
        if (isExtensionContext()) {
            // Extension context: use chrome.storage.local
            const chrome = (window as unknown as Record<string, unknown>)
                .chrome as Chrome
            const result = await new Promise<
                Record<string, string | undefined>
            >((resolve, reject) => {
                chrome.storage.local.get(THEME_STORAGE_KEY, (result) => {
                    if (chrome.runtime?.lastError) {
                        reject(new Error(chrome.runtime.lastError.message))
                    } else {
                        resolve(result)
                    }
                })
            })
            const stored = result[THEME_STORAGE_KEY]
            if (stored === 'light') return false
            if (stored === 'dark') return true
            return defaultTheme === 'dark'
        } else {
            // Web app: use localStorage
            const stored = localStorage.getItem(THEME_STORAGE_KEY)
            if (stored === 'light') return false
            if (stored === 'dark') return true
            return defaultTheme === 'dark'
        }
    } catch (error) {
        console.warn('Failed to get theme from storage:', error)
        return defaultTheme === 'dark'
    }
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
    children,
    defaultTheme = 'dark',
}) => {
    const [isDark, setIsDark] = useState(defaultTheme === 'dark') // Initial default, updated async
    const theme = isDark ? darkTheme : lightTheme

    const toggleTheme = useCallback(() => {
        setIsDark((prev) => !prev)
    }, [])

    const setTheme = useCallback((newTheme: ThemeType) => {
        setIsDark(newTheme === 'dark')
    }, [])

    // Load stored theme async
    useEffect(() => {
        getStoredTheme(defaultTheme).then((storedIsDark) => {
            setIsDark(storedIsDark)
        })
    }, [defaultTheme])

    // Persist theme changes to storage
    useEffect(() => {
        const themeToStore = isDark ? 'dark' : 'light'
        try {
            if (isExtensionContext()) {
                // Extension: use chrome.storage.local
                const chrome = (window as unknown as Record<string, unknown>)
                    .chrome as Chrome
                new Promise<void>((resolve, reject) => {
                    chrome.storage.local.set(
                        { [THEME_STORAGE_KEY]: themeToStore },
                        () => {
                            if (chrome.runtime?.lastError) {
                                reject(
                                    new Error(chrome.runtime.lastError.message),
                                )
                            } else {
                                resolve()
                            }
                        },
                    )
                }).catch((error) => {
                    console.warn(
                        'Failed to save theme to chrome.storage.local:',
                        error,
                    )
                })
            } else {
                // Web app: use localStorage
                localStorage.setItem(THEME_STORAGE_KEY, themeToStore)
            }
        } catch (error) {
            console.warn('Failed to save theme preference to storage:', error)
        }
    }, [isDark])

    return (
        <ThemeContext.Provider
            value={{
                theme,
                isDark,
                toggleTheme,
                setTheme,
            }}
        >
            <StyledThemeProvider theme={theme}>{children}</StyledThemeProvider>
        </ThemeContext.Provider>
    )
}

export { ThemeContext }

/**
 * Hook to get the theme context.
 */
export function useTheme(): ThemeContextType {
    const context = useContext(ThemeContext)
    if (context == null) {
        throw new Error('useTheme must be used within a ThemeProvider')
    }
    return context
}

/**
 * Hook to get just the theme object (for styled-components).
 */
export function useStyledTheme(): DefaultTheme {
    const { theme } = useTheme()
    return theme
}
